import React, { useEffect, useRef, useMemo } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

import InvalidForm  from 'src/components/InvalidForm'
import InputField   from 'src/components/InputField'
import RadioList    from 'src/components/RadioList'
import CheckBox     from 'src/components/CheckBox'
import ColorPicker  from 'src/components/ColorPicker'
import Thumb        from 'src/fancy/waves/Thumb'

const Form = ({
  errors, fancyBackgrounds, imagePlacements, impactColors,
  inputProps, booleanProps, pickFile, handleSubmit,
  textToDonate, editUrl, state, snapshot, resetForm, canUploadAndSave, saved,
}) => {
  const errorElement = useRef()

  const dirty = useMemo(() =>
    JSON.stringify(state) != JSON.stringify(snapshot)
  , [ state, snapshot ])

  const baseError = errors.base?.[0]

  useEffect(() => {
    if (baseError)
      errorElement.current.scrollIntoView()
  }, [baseError])

  const fancyBackgroundProps = inputProps('fancy_background_id')
  const backgroundImageProps = inputProps('background_image')

  return (
    <form noValidate onSubmit={handleSubmit}>
      <InvalidForm ref={errorElement}>{baseError}</InvalidForm>

      <fieldset className="mb-4">
        <legend className="h4 float-none">Theme</legend>

        {
          !!fancyBackgrounds.length &&
          <fieldset className="mb-3">
            <legend className="form-label float-none fs-6">Background</legend>
            <div className="d-grid gap-3" style={{
              gridTemplateColumns: 'repeat(auto-fill, minmax(clamp(9rem, 47.5%, 10rem), 1fr))'
            }}>
              {fancyBackgrounds.map(({ id, name, thumb, config }) =>
                <div key={id}>
                  <input
                    type="radio"
                    className="visually-hidden fancy-background-radio"
                    name="fancy_background_id"
                    id={`fancy_background_${id}`}
                    value={id}
                    checked={state.fancy_background_id == id}
                    onChange={fancyBackgroundProps.onChange}
                  />
                  <label
                    htmlFor={`fancy_background_${id}`}
                    className="w-100 rounded-2 overflow-hidden fancy-background-label"
                  >
                    <Thumb thumb={thumb} config={config} width={304} height={171} />
                    <div className="px-3 py-2">{name}</div>
                  </label>
                </div>
              )}
              <div>
                <input
                  type="radio"
                  className="visually-hidden fancy-background-radio"
                  name="fancy_background_id"
                  id="fancy_background_null"
                  value=""
                  checked={state.fancy_background_id == ''}
                  onChange={fancyBackgroundProps.onChange}
                />
                <label
                  htmlFor="fancy_background_null"
                  className="w-100 rounded-2 overflow-hidden fancy-background-label"
                >
                  <div className="ratio ratio-16x9 bg-light">
                    <div className="d-flex align-items-center justify-content-center">
                      <FontAwesomeIcon icon="sliders" size="3x" />
                    </div>
                  </div>
                  <div className="px-3 py-2">Custom...</div>
                </label>
              </div>
            </div>
          </fieldset>
        }

        {
          !state.fancy_background_id &&
          <InputField
            {...inputProps('background_color')}
            label="Background color"
            type={ColorPicker}
            options={impactColors}
          />
        }

        {
          !state.fancy_background_id &&
          (snapshot.background_image || canUploadAndSave) &&
          <div className="mb-3">
            <label className="form-label" htmlFor="background_image">
              Background image
            </label>

            <div className="d-flex flex-wrap gap-1">
              {
                canUploadAndSave &&
                <button
                  type="button"
                  id="background_image"
                  onClick={pickFile}
                  className="btn btn-outline-primary"
                >
                  <FontAwesomeIcon icon="upload" className="me-2" />
                  {
                    backgroundImageProps.value
                    ? 'Change Image'
                    : 'Add Image'
                  }
                </button>
              }
              {
                backgroundImageProps.value &&
                <button
                  type="button"
                  onClick={backgroundImageProps.onChange}
                  className="btn btn-danger"
                >
                  <FontAwesomeIcon icon="trash" className="me-2" />
                  Remove Image
                </button>
              }
              {
                !backgroundImageProps.value && snapshot.background_image &&
                <button
                  type="button"
                  onClick={() => backgroundImageProps.onChange({
                    target: { value: snapshot.background_image }
                  })}
                  className="btn btn-outline-secondary"
                >
                  <FontAwesomeIcon icon="undo" className="me-2" />
                  Undo Remove Image
                </button>
              }
            </div>

            {
              backgroundImageProps.error &&
              <div className="invalid-feedback d-block">
                {backgroundImageProps.error}
              </div>
            }

            {
              canUploadAndSave &&
              <div className="form-text">
                We recommend a 16:9 aspect ratio and a resolution of at least 1920 x 1080 pixels.
              </div>
            }
          </div>
        }

        {
          !state.fancy_background_id &&
          state.background_image &&
          <RadioList
            {...inputProps('background_image_placement')}
            legend="Image placement"
            options={imagePlacements}
          />
        }

        {
          !state.fancy_background_id &&
          <CheckBox
            {...booleanProps('confetti')}
            label="Rain down hearts"
          />
        }
      </fieldset>

      <fieldset className="mb-4">
        <legend className="h4 float-none">Donor Feed</legend>

        <RadioList
          {...inputProps('show_full_name')}
          legend="Donor name"
          options={[
            { label: 'First name and last initial', value: false },
            { label: 'Full name', value: true },
          ]}
        />

        <CheckBox
          {...booleanProps('display_donation_amounts')}
          label="Display donation amounts"
        />
      </fieldset>

      <fieldset className="mb-4">
        <legend className="h4 float-none">Call to Action</legend>

        {
          (textToDonate || canUploadAndSave) &&
          <CheckBox
            {...booleanProps('display_ttd')}
            label="Text-to-Donate"
            hint={
              !textToDonate && canUploadAndSave &&
              <span>
                Activate your TTD keyword in the
                {' '}
                <a href={editUrl} target="_blank">edit</a> tab.
              </span>
            }
          />
        }

        <CheckBox
          {...booleanProps('display_qr_code')}
          label="QR code"
        />
      </fieldset>

      <div className="d-flex gap-1 position-relative">
        {
          canUploadAndSave &&
          <button
            disabled={!dirty}
            className="btn btn-primary"
          >Save</button>
        }
        <button
          type="button"
          disabled={!dirty}
          className="btn btn-light"
          onClick={resetForm}
        >Reset</button>

        <div
          className="position-absolute top-0 start-0 bg-white fw-bold p-2"
          style={{
            opacity: Number(saved),
            transition: 'opacity 0.3s',
            pointerEvents: 'none',
            width: '8rem',
          }}
        >
          Saved!
        </div>
      </div>
    </form>
  )
}

export default Form
